import type { Branch, BranchHour, CreateBranch, UpdateBranch } from "@dentacore/shared-types";

import { api } from "./client";

/**
 * The clinic's branches. **Clinic-scoped, not branch-scoped** — listing them
 * is how you pick one, so the header a branch-scoped call needs cannot be a
 * precondition for this.
 */
export function listBranches(signal?: AbortSignal): Promise<Branch[]> {
  return api<Branch[]>("/branches", { signal });
}

/**
 * Rename a branch, move its timezone, or change how to reach it. **Admin
 * only** — the server refuses anyone else with a 403 before this ever
 * resolves.
 *
 * **Turnover is not here.** It belongs to the chair since 2026-09-23, and is
 * changed with `updateChair`.
 */
export function updateBranch(branchId: string, body: UpdateBranch): Promise<Branch> {
  return api<Branch>(`/branches/${branchId}`, { method: "PATCH", body });
}

/** Open a branch. **Admin only**, same as the update above. */
export function createBranch(body: CreateBranch): Promise<Branch> {
  return api<Branch>("/branches", { method: "POST", body });
}

/**
 * A branch's opening hours — all seven weekdays, always.
 *
 * Clinic-scoped like the branch itself, so no active-branch header is needed
 * and a location can be configured without switching to it.
 */
export function listBranchHours(branchId: string, signal?: AbortSignal): Promise<BranchHour[]> {
  return api<BranchHour[]>(`/branches/${branchId}/hours`, { signal });
}

/**
 * Replace a branch's week. **Admin only.**
 *
 * A replace rather than a patch, matching the endpoint: the editor is a whole
 * week and its save button submits one, so there is no partial state for a
 * per-day call to represent.
 */
export function setBranchHours(branchId: string, days: BranchHour[]): Promise<BranchHour[]> {
  return api<BranchHour[]>(`/branches/${branchId}/hours`, { method: "PUT", body: { days } });
}
